import React, { useState, useMemo, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import Core from "../../../common/clientSdk";
import { ShimmerBox } from '../../../components';
import Utils from '../../../common/utils';



/**
 * TimelineSection - Campaign timeline frequency chart
 * @param {object} sectionRef - Ref for scroll navigation
 * @param {object} campaignDetail - Campaign details including dates
 * @param {array} contentData - Content data with platform information (for line chart)
 * @param {array} influencerList - Assignment influencer list data (for bar chart)
 */
const TimelineSection = ({
    sectionRef,
    campaignDetail,
    contentData,
    isVisible,
    is_public,
    shareKey,
    campaignId,
    isReportGenerated = true,
    isLoading: parentLoading,
    refreshTrigger = 0,
    isSharedView
}) => {
    const [fetchedContent, setFetchedContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter by platform if active (not used for now but helper kept)
    // const [platformFilter, setPlatformFilter] = useState("All");

    const sanitizeMetrics = (data) => {
        if (data === null || data === undefined) return data;
        if (Array.isArray(data)) {
            return data.map((item) => sanitizeMetrics(item));
        }
        if (typeof data === "object") {
            const sanitized = { ...data };
            Object.keys(sanitized).forEach((key) => {
                const value = sanitized[key];
                if (
                    typeof value === "number" ||
                    (typeof value === "string" &&
                        value.length > 0 &&
                        !isNaN(Number(value)) &&
                        /^-?\d+(\.\d+)?$/.test(value))
                ) {
                    const num = Number(value);
                    if (num <= 0) {
                        sanitized[key] = 0;
                    }
                } else if (typeof value === "object" && value !== null) {
                    sanitized[key] = sanitizeMetrics(value);
                }
            });
            return sanitized;
        }
        return data;
    };

    // Get campaign start date and end date (or current date if not expired)
    const campaignData = campaignDetail?.campaign_data || {};
    const startTimestamp = campaignData?.date_added || campaignData?.campaign_start_date;
    const endTimestamp = campaignData?.end_date || campaignData?.campaign_end_date;

    const startDate = startTimestamp ? new Date(startTimestamp * 1000) : new Date();
    const now = new Date();
    const endDate = endTimestamp ? new Date(Math.min(endTimestamp * 1000, now.getTime())) : now;

    // Use content from props if provided, otherwise fallback to fetched results
    // const effectiveContent = (contentData) ? contentData : fetchedContent; // Not used anymore as we sync fetchedContent with prop
    const isExternalLoading = parentLoading !== undefined ? parentLoading : loading;

    // Fetch assignment influencer list (contains both influencer info and content analytics)
    useEffect(() => {
        // If contentData is provided, we sync fetchedContent with prop
        if (contentData && contentData.length > 0) {
            setFetchedContent(sanitizeMetrics(contentData));
            setLoading(false);
            return;
        }

        // Parent still loading assignment list — avoid duplicate fetch; chart uses parentLoading for shimmer
        if (Array.isArray(contentData) && contentData.length === 0 && parentLoading) {
            return;
        }

        // If contentData is provided but empty, and we are not in loading state, stop
        if (contentData && contentData.length === 0 && !parentLoading) {
            setFetchedContent([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        if (is_public && shareKey) {
            const params = { share_key: shareKey };
            Core.getPublicAssignmentInfluencerList(
                (response) => {
                    const payload = response?.data || response?.assignments || response?.response || [];
                    setFetchedContent(sanitizeMetrics(payload));
                    setLoading(false);
                },
                (err) => {
                    setError(err);
                    setFetchedContent([]);
                    setLoading(false);
                },
                (inProgress) => setLoading(inProgress),
                params
            );
        } else {
            const id = campaignId || campaignDetail?.campaign_id || campaignDetail?.id || campaignDetail?.campaign_id_string;
            if (!id) return;

            const params = { campaign_id: id };
            Core.getAssignmentInfluencerList(
                (err, response) => {
                    if (err) {
                        setError(err);
                        setFetchedContent([]);
                        setLoading(false);
                        return;
                    }
                    const payload = response?.data || response?.assignments || response?.response || [];
                    setFetchedContent(sanitizeMetrics(payload));
                    setLoading(false);
                },
                (err) => {
                    setError(err);
                    setFetchedContent([]);
                    setLoading(false);
                },
                (inProgress) => setLoading(inProgress),
                params
            );
        }
    }, [campaignDetail, campaignId, shareKey, is_public, contentData, refreshTrigger, parentLoading]);

    // Remove old separate timeline trends fetch as we get everything from assignment list now



    // Process timeline data for Bar Chart (Assignment Frequency)
    // Using content_posted_on from content_analytics to determine dates
    const chartData = useMemo(() => {
        if (!fetchedContent || fetchedContent.length === 0) return [];

        const aggregatedByDate = {}; // { YYYY-MM-DD: { date: Date, influencers: Set } }

        fetchedContent.forEach(item => {
            const influencerName = item?.influencer_data?.name || item?.influencer_data?.influencer_name || "Unknown";
            const analyticsMap = item?.content_analytics || {};
            const contentKeys = Object.keys(analyticsMap);

            if (contentKeys.length === 0) return;

            contentKeys.forEach(ck => {
                const data = analyticsMap[ck];
                // Prioritize content_posted_on (epoch) for the chart as per user request
                const timestamp = data.content_posted_on;
                const creationDay = data.creation_day;
                
                let dateObj = null;
                if (timestamp) {
                    dateObj = new Date(timestamp * 1000);
                } else if (creationDay) {
                    dateObj = new Date(creationDay);
                }

                if (!dateObj || isNaN(dateObj.getTime())) return;

                const dateKey = formatDateKey(dateObj);

                if (!aggregatedByDate[dateKey]) {
                    aggregatedByDate[dateKey] = {
                        date: dateObj,
                        influencers: new Set()
                    };
                }
                aggregatedByDate[dateKey].influencers.add(influencerName);
            });
        });

        const result = Object.entries(aggregatedByDate).map(([key, data]) => ({
            date: data.date,
            dateLabel: formatDateLabel(data.date),
            dateKey: key,
            value: data.influencers.size,
            influencers: Array.from(data.influencers)
        }));

        // Sort by date
        return result.sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [fetchedContent]);

    // Process performance trends (timeline) data for Line Chart
    // Using creation_day from content_analytics to group metrics
    const lineChartData = useMemo(() => {
        if (!fetchedContent || fetchedContent.length === 0) return [];

        const aggregated = {};

        fetchedContent.forEach(item => {
            const analyticsMap = item?.content_analytics || {};
            const contentKeys = Object.keys(analyticsMap);

            if (contentKeys.length === 0) return;

            contentKeys.forEach(ck => {
                const data = analyticsMap[ck];
                const day = data.creation_day;
                const timestamp = data.content_posted_on;

                // Prioritize content_posted_on (epoch) for grouping as per user request
                let dateStr = null;
                if (timestamp) {
                    const d = new Date(timestamp * 1000);
                    dateStr = formatDateKey(d);
                } else if (day) {
                    dateStr = day;
                }

                if (!dateStr) return;

                if (!aggregated[dateStr]) {
                    aggregated[dateStr] = {
                        dateStr: dateStr,
                        views: 0,
                        engagement: 0,
                        likes: 0,
                        comments: 0,
                        shares: 0,
                        saves: 0
                    };
                }

                const views = Number(data.view_count || data.views || 0);
                
                // Use Math.max(0, ...) to handle negative values like -1
                const likes = Math.max(0, Number(data.like_count || data.likes || 0));
                const comments = Math.max(0, Number(data.comment_count || data.comments || 0));
                const shares = Math.max(0, Number(data.share_count || data.shares || data.share || 0));
                const saves = Math.max(0, Number(data.save_count || data.saves || 0));

                aggregated[dateStr].views += views;
                aggregated[dateStr].likes += likes;
                aggregated[dateStr].comments += comments;
                aggregated[dateStr].shares += shares;
                aggregated[dateStr].saves += saves;
                aggregated[dateStr].engagement += (likes + comments + shares + saves);
            });
        });

        const extractedPoints = Object.values(aggregated).map(item => {
            const dateObj = new Date(item.dateStr);
            return {
                ...item,
                dateEpoch: dateObj.getTime(),
                dateLabel: formatDateLabel(dateObj)
            };
        });

        // Sort by timestamp
        return extractedPoints.sort((a, b) => a.dateEpoch - b.dateEpoch);
    }, [fetchedContent]);

    // Calculate max value for Y-axis scaling (Bar Chart)
    const maxValue = useMemo(() => {
        let max = 0;
        chartData.forEach(item => {
            const total = item.value || 0;
            if (total > max) max = total;
        });
        return Math.max(max, 1);
    }, [chartData]);

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const item = payload[0].payload;

        return (
            <div className="chart-tooltip">
                <div className="tooltip-content">
                    <div className="tooltip-row">
                        <strong>
                            Influencers ({item?.influencers ? item.influencers.length : 0})
                        </strong>
                    </div>
                    {item?.influencers && item.influencers.length > 0 ? (
                        item.influencers.map((name, i) => (
                            <div className="tooltip-row" key={i}>
                                <span>{name}</span>
                            </div>
                        ))
                    ) : (
                        <div className="tooltip-row">
                            <span>No influencers</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="timeline-tab section-content" ref={sectionRef}>
            <div className="section-header">
                <h3>Campaign Timeline Frequency</h3>
                {!is_public && !isSharedView && isVisible !== undefined && (
                    <div className="section-controls sharing-status-container">
                        <span className="sharing-status">Sharing status</span>
                        {isVisible ? (
                            <span className="visible-badge">Visible</span>
                        ) : (
                            <span className="hidden-badge">Hidden</span>
                        )}
                    </div>
                )}
            </div>

            <div className="timeline-chart">
                {!isReportGenerated ? (
                    <div style={{ padding: '40px', textAlign: 'center', borderRadius: '12px', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px dashed #e5e7eb', margin: '20px 0' }}>
                        <h3 style={{ fontSize: '16px', color: '#6B7280', fontWeight: 600 }}>Report not generated yet</h3>
                        <p style={{ color: '#9CA3AF', marginTop: '8px', fontSize: '14px' }}>Click 'Generate Report' to view this data.</p>
                    </div>
                ) : (
                        <div className="chart-bars-wrapper" style={{ width: '100%', height: 300 }}>
                            {isExternalLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', padding: '20px' }}>
                                    {/* Chart bars shimmer */}
                                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '16px', height: '220px', padding: '0 20px' }}>
                                        {[100, 60, 80, 120, 40, 100, 70, 90].map((h, i) => (
                                            <ShimmerBox key={i} width="36px" height={`${h}px`} borderRadius="6px 6px 0 0" />
                                        ))}
                                    </div>
                                    {/* X-axis shimmer */}
                                    <div style={{ display: 'flex', justifyContent: 'space-around', gap: '16px', padding: '0 20px' }}>
                                        {[...Array(8)].map((_, i) => (
                                            <ShimmerBox key={i} width="36px" height="12px" />
                                        ))}
                                    </div>
                                </div>
                            ) : chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} barCategoryGap={24} margin={{ top: 10, right: 20, bottom: 25, left: 15 }}>
                                        <XAxis
                                            dataKey="dateLabel"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fill: '#6B7280', fontSize: 11 }}
                                            label={{ value: "Content Posted Date", position: "insideBottom", offset: -15, fill: "#6B7280", fontSize: 12 }}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            domain={[0, 'dataMax + 1']}
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fill: '#6B7280', fontSize: 11 }}
                                            label={{ value: "Influencers", angle: -90, position: "insideLeft", fill: "#6B7280", fontSize: 12, style: { textAnchor: 'middle' } }}
                                        />
                                        <Tooltip content={renderTooltip} cursor={{ fill: 'rgba(107, 114, 128, 0.08)' }} />
                                        <Bar
                                            dataKey="value"
                                            fill="#6c7cff"
                                            radius={[6, 6, 0, 0]}
                                            barSize={36}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    background: '#F9FAFB',
                                    borderRadius: '8px',
                                    color: '#9CA3AF'
                                }}>
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>No posting data available yet</span>
                                </div>
                            )}
                        </div>
                )}

                {/* single-series chart — no legend required */}
                {/* Line Chart Section */}
                {/* {isReportGenerated && (
                    <div className="section-header" style={{ marginTop: '40px' }}>
                        <h3>Performance Trends</h3>
                    </div>
                )} */}

                {/* {isReportGenerated && (
                    <div className="timeline-chart">
                        <div className="chart-container">
                              <div className="chart-bars-wrapper" style={{ width: '100%', height: 300 }}>
                                {trendsLoading ? (
                                    <ShimmerBox width="100%" height="100%" />
                                ) : lineChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={lineChartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" /> */}
                                            {/* <XAxis
                                                dataKey="dateLabel"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                dy={10}
                                            /> */}
                                            {/* Left Axis for Views */}
                                            {/* <YAxis
                                                yAxisId="left"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                tickFormatter={(value) => Utils.changeNumberFormate(value)}
                                                label={{ value: 'Views', angle: -90, position: 'insideLeft', fill: '#8884d8', fontSize: 12 }}
                                            /> */}
                                            {/* Right Axis for Engagement */}
                                            {/* <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                tickFormatter={(value) => Utils.changeNumberFormate(value)}
                                                label={{ value: 'Engagement', angle: 90, position: 'insideRight', fill: '#00C49F', fontSize: 12 }}
                                            />
                                            <Tooltip
                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                                formatter={(value) => [Utils.changeNumberFormate(value), ""]}
                                            />
                                            <Legend verticalAlign="top" height={36} />
                                            <Line yAxisId="left" type="monotone" dataKey="views" stroke="#8884d8" name="Views" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                            <Line yAxisId="right" type="monotone" dataKey="engagement" stroke="#00C49F" name="Engagement" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#9CA3AF' }}>
                                        No trend data available
                                    </div>
                                )}
                             </div>
                        </div>
                    </div>
                )} */}
            </div>
        </div>
    );
};

// Helper function to format date as key (YYYY-MM-DD)
function formatDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Helper function to format date label (DD Mon YY)
function formatDateLabel(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day} ${month} ${year}`;
}

// Helper function to determine platform type from platform_id or content_type_id
function getPlatformType(platformId, contentTypeId) {
    // Instagram platform IDs
    const instagramPlatformIds = ['btll', 'instagram'];
    // YouTube platform IDs
    const youtubePlatformIds = ['nxjo', 'youtube'];

    // Instagram content type IDs
    const instagramContentTypes = ['s1xl', 'tqym', 'reel', 'post', 'story', 'REEL', 'POST', 'STORY'];
    // YouTube content type IDs
    const youtubeContentTypes = ['knxh', 'znht', 'short', 'video', 'shorts', 'SHORT', 'VIDEO', 'SHORTS'];

    // Check platform ID
    if (platformId) {
        const normalizedPlatformId = String(platformId).toLowerCase();
        if (instagramPlatformIds.includes(normalizedPlatformId)) return 'instagram';
        if (youtubePlatformIds.includes(normalizedPlatformId)) return 'youtube';
    }

    // Check content type ID
    if (contentTypeId) {
        const normalizedContentType = String(contentTypeId).toLowerCase();
        if (instagramContentTypes.some(type => normalizedContentType.includes(type.toLowerCase()))) return 'instagram';
        if (youtubeContentTypes.some(type => normalizedContentType.includes(type.toLowerCase()))) return 'youtube';
    }

    return null;
}

export default TimelineSection;
